import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Loader from 'components/layout/Loader';
// import Spinner from 'components/Spinner';
import { intlModule } from '@chedri/base';
import DatePicker from 'components/forms/DatePicker';
import { Button, Col, Row } from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';
import ListFilterWhiteLabel from 'components/layout/list/filters/ListFilterWhiteLabel';

const LoginStats = () => {
  const messages = useSelector(intlModule.selectors.getMessages);
  const { widget_login_stats: messagesWls, widget_order_stats: messagesWos, loading: loadingText } = messages;
  const { widget_title: widgetTitle, number_of_logins_label_title: labelTitle } = messagesWls;
  const [countLogins, setCountLogins] = useState(null);
  const [dateA, setDateA] = useState(moment().startOf('month'));
  const [dateB, setDateB] = useState(new Date());
  const [loadedStartDate, setLoadedStartDate] = useState(null);
  const [loadedEndDate, setLoadedEndDate] = useState(null);
  const [abortController, setAbortController] = useState(null);
  const [filterWhiteLabels, setFilterWhiteLabels] = useState([]);
  const appIntl = useSelector(({ intl }) => intl);
  const user = useSelector(({ login: { user } }) => user);
  const timezone =
    Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone ?? user?.profile?.timezone ?? 'Europe/Berlin';

  const [dateFrom, dateTo] = dateA < dateB ? [dateA, dateB] : [dateB, dateA];

  const colorCount = '#8884d8';

  const listFilterWhiteLabelsRef = useRef();
  const saveWhiteLabelRef = ref => {
    console.log('saveWhiteLabelRef');
    listFilterWhiteLabelsRef.current = ref;
  };

  const whiteLabelsUpdated = whiteLabelsDict => {
    const ids = Object.keys(whiteLabelsDict).sort();
    setFilterWhiteLabels(ids);
    console.log('companiesUpdated', { current: listFilterWhiteLabelsRef?.current });
  };

  useEffect(() => {
    const start = moment(dateFrom)
      .startOf('day')
      .format();
    const end = moment(dateTo)
      .endOf('day')
      .format();

    if (abortController) {
      // cancel previous request that is still running
      abortController.abort();
    }
    const controller = new AbortController();
    const { signal } = controller;
    setAbortController(controller);

    const params = { start, end };
    if (filterWhiteLabels.length > 0) {
      params.whiteLabels = filterWhiteLabels;
    }

    axios
      .get('/api/loginstats.json', { params, signal })
      .then(({ data }) => {
        const { count, start_date: startDate, end_date: endDate } = data;
        setCountLogins(count);
        setLoadedStartDate(new Date(startDate));
        setLoadedEndDate(new Date(endDate));
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => {
        setAbortController(null);
      });
  }, [dateFrom, dateTo, filterWhiteLabels]);

  const dateFormatter = new Intl.DateTimeFormat('de-DE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const isDatesLoaded = loadedStartDate && loadedEndDate;
  let subtitle = isDatesLoaded
    ? `${dateFormatter.format(loadedStartDate)} — ${dateFormatter.format(loadedEndDate)}`
    : loadingText;

  const dateUpdate = (name, moment) => {
    console.log('dateUpdate', { name, moment, date: moment.toDate() });
    if (name === 'dateFrom') {
      setDateA(moment);
    } else if (name === 'dateTo') {
      setDateB(moment);
    } else {
      console.error('LoginStats.js dateUpdate() Unknown name', { name });
    }
  };

  const quickButtons = () => {
    const lastNMonths = n => ({
      label: messagesWos[`last_${n}_months`],
      range: [
        moment()
          .startOf('month')
          .subtract(n, 'months'),
        moment(),
      ],
    });

    const times = [
      { label: messagesWos.this_month, range: [moment().startOf('month'), moment().endOf('day')] },
      {
        label: messagesWos.previous_month,
        range: [
          moment()
            .startOf('month')
            .subtract(1, 'months'),
          moment().startOf('month'),
        ],
      },
      lastNMonths(3),
      lastNMonths(6),
      lastNMonths(12),
    ];

    const setSelectedRange = ([dateA, dateB]) => () => {
      setDateA(dateA);
      setDateB(dateB);
    };

    return times.map(({ label, range }, i) => (
      <Fragment key={label}>
        <Button
          bsSize="small"
          bsStyle="info"
          onClick={setSelectedRange(range)}
          style={{ marginRight: i < times.length - 1 ? '5px' : '', marginBottom: '5px' }}
        >
          <i className="fas fa-calendar" /> {label}
        </Button>
      </Fragment>
    ));
  };

  const bigFigure = (title, value, color) => (
    <div style={{ textAlign: 'center', height: '100px', paddingTop: '25px', border: '1px solid #CCC' }}>
      <h4>
        {title}: <span style={{ color, fontSize: '1.6rem' }}>{value}</span>
      </h4>
    </div>
  );

  return (
    <>
      <div className="widget-11-2 card no-border card-condensed widget-loader-circle full-height d-flex flex-column">
        <div className="padding-25">
          <h2 className="no-margin">
            {widgetTitle}
            {abortController && (
              <>
                {' '}
                <i className="fad fa-spinner-third fa-spin" />
              </>
            )}
          </h2>
          <p className="no-margin">{subtitle}</p>
        </div>
        <div className="padding-25">
          <Row style={{ marginBottom: '25px' }}>
            {loadedStartDate && loadedEndDate ? (
              <>
                <Col xs={12} md={6}>
                  {bigFigure(labelTitle, countLogins, colorCount)}
                </Col>
              </>
            ) : (
              <Col xs={12}>
                <Loader />
              </Col>
            )}
          </Row>
          <Row>
            <Col xs={6} sm={3}>
              <DatePicker
                name="dateFrom"
                value={dateFrom}
                displayTz={timezone}
                returnTz={timezone}
                locale={appIntl.locale}
                onChange={dateUpdate}
                label={messages.date_from}
              />
            </Col>
            <Col xs={6} sm={3}>
              <DatePicker
                name="dateTo"
                value={dateTo}
                displayTz={timezone}
                returnTz={timezone}
                locale={appIntl.locale}
                onChange={dateUpdate}
                label={messages.date_to}
              />
            </Col>
            <Col xs={12} sm={6}>
              {quickButtons()}
            </Col>
            {user.username === 'CreacheckAdmin' && (
              <Col xs={12} sm={2}>
                <ListFilterWhiteLabel
                  updateLocation={false}
                  onUpdate={whiteLabelsUpdated}
                  listName="myWhiteLabelProject"
                  saveRef={saveWhiteLabelRef}
                />
              </Col>
            )}
          </Row>
        </div>
      </div>
    </>
  );
};

export default LoginStats;
